import { useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { useGetIncompleteEmbedMediaListingsQuery } from "../../services/embed-media.service";
import type { HostHubVideoTourListing } from "../types/model/video-tour-listing";
import { toVideoTourListings } from "../utils/host-hub-video-tour.utils";

import useHostHubOwnerListings from "./useHostHubOwnerListings";

type UseHostHubIncompleteVideoTourListingsResult = {
  isError: boolean;
  isLoading: boolean;
  listings: HostHubVideoTourListing[];
};

/**
 * Fetches the host's listings missing a video/3D tour and derives the (capped)
 * rows for the "Add video tours to your listings" recommendation.
 *
 * 1. `GET /v2/spaces-v2/listings/embed-media/incomplete` → listing IDs w/o video
 * 2. `GET /v2/spaces-v2/listings/owner/:sso-id?full=true` → titles, types, images
 */
const useHostHubIncompleteVideoTourListings =
  (): UseHostHubIncompleteVideoTourListingsResult => {
    const ssoId = useSelector(selectProfileSSOid);

    const ownerListings = useHostHubOwnerListings();

    const incompleteMediaQuery = useGetIncompleteEmbedMediaListingsQuery(
      ssoId ? undefined : skipToken,
    );

    const listings = useMemo(
      () =>
        toVideoTourListings({
          incompleteListingIds: incompleteMediaQuery.data?.listingIds,
          listings: ownerListings.listings,
        }),
      [ownerListings.listings, incompleteMediaQuery.data],
    );

    const isLoading = ownerListings.isLoading || incompleteMediaQuery.isLoading;

    const isError = ownerListings.isError || incompleteMediaQuery.isError;

    return {
      isError,
      isLoading,
      listings,
    };
  };

export default useHostHubIncompleteVideoTourListings;
